import { createAction } from '@reduxjs/toolkit';
import { Moment } from 'moment';
import { AutomationSource } from 'api/automation-builder/types';
import { Automation } from 'api/automations';
import { Team, User } from 'api/models';
import type { FilterableField } from 'api/tasks/filterable-field';
import { Filter } from 'components/molecules/filters/types';
import { SquadValue } from 'components/molecules/squad-picker';
import { PositionData } from 'components/organisms/templates/projects/commons/rearrange/types';
// import { SheetTemplateData } from 'components/pages/templates/newTemplateData';
import { TemplateState } from '../api';
import { BulkActionPayloadMap } from './bulk-action-types';
import {
  InsertDirection,
  MilestoneRevenueRecognitionConfigs,
  ProjectTemplateApi,
  ProjectTemplateProjectPhase,
  ProjectTemplateSpace,
  ProjectTemplateSprint,
  ProjectTemplateTask,
} from './types';

interface BaseActionPayload {
  handleFailure?(): void;
  handleAlways?(): void;
}

export const fetchProjectTemplate = createAction<{
  templateId: number;
  dynamicConditionFields: FilterableField[];
}>('@@TEMPLATES/PROJECT/fetchProjectTemplate');

export interface SaveProjectTemplatePayload extends BaseActionPayload {
  type: TemplateState;
  handleSuccess?(template: ProjectTemplateApi): void;
  saveToIdb?: boolean;
  successCreatedMessage?: string;
  successSavedMessage?: string;
  errorMessage?: string;
}

export interface SaveProjectTemplateWithSheetTabDataPayload
  extends BaseActionPayload {
  sheetTabId: number;
  sheetTabName: string;
  assignee: SquadValue;
  taskId: number;
}

export const saveProjectTemplate = createAction<SaveProjectTemplatePayload>(
  '@@TEMPLATES/PROJECTS/saveProjectTemplate',
);

// ---- SHEET TAB ---- //

export const saveProjectTemplateWithSheetTabData =
  createAction<SaveProjectTemplateWithSheetTabDataPayload>(
    '@@TEMPLATES/PROJECTS/saveProjectTemplateWithSheetTabData',
  );

// ---- PHASE ---- //
export interface CreatePhaseInProjectTemplatePayload extends BaseActionPayload {
  projectPhase: Partial<ProjectTemplateProjectPhase>;
  insertAt?: {
    id: number;
    direction: InsertDirection.Above | InsertDirection.Below;
  };
  handleSuccess?(
    projectPhase: AtLeast<ProjectTemplateProjectPhase, 'projectPhaseId'>,
  ): void;
}
export const createPhaseInProjectTemplate =
  createAction<CreatePhaseInProjectTemplatePayload>(
    '@@TEMPLATES/PROJECT/createPhaseInProjectTemplate',
  );

export interface UpdatePhaseInProjectTemplatePayload extends BaseActionPayload {
  projectPhaseId: number;
  payload: Partial<ProjectTemplateProjectPhase>;
  handleSuccess?(): void;
}
export const updatePhaseInProjectTemplate =
  createAction<UpdatePhaseInProjectTemplatePayload>(
    '@@TEMPLATES/PROJECT/updatePhaseInProjectTemplate',
  );

export interface DeletePhaseInProjectTemplate extends BaseActionPayload {
  projectPhaseId: number;
  handleSuccess?(): void;
}
export const deletePhaseInProjectTemplate =
  createAction<DeletePhaseInProjectTemplate>(
    '@@TEMPLATES/PROJECT/deletePhaseInProjectTemplate',
  );
export interface AddPhaseAboveInProjectTemplatePayload
  extends BaseActionPayload {
  projectPhaseId: number;
}

export const addPhaseAboveInProjectTemplate =
  createAction<AddPhaseAboveInProjectTemplatePayload>(
    '@@TEMPLATES/PROJECT/addPhaseAboveInProjectTemplate',
  );

export interface ShiftStartDayOfProjectPhaseInProjectTemplatePayload
  extends BaseActionPayload {
  projectPhaseId: number;
  shiftBy: number;
}

export const shiftStartDayOfProjectPhaseInProjectTemplate =
  createAction<ShiftStartDayOfProjectPhaseInProjectTemplatePayload>(
    '@@TEMPLATES/PROJECT/shiftStartDayProjectPhaseInProjectTemplate',
  );

// ---- TASK ---- //
export interface CreateTaskInProjectTemplate extends BaseActionPayload {
  projectPhaseId: number;
  payload?: {
    taskId: number;
    taskName?: string;
  };
}
export const createTaskInProjectTemplate =
  createAction<CreateTaskInProjectTemplate>(
    '@@TEMPLATES/PROJECT/createTaskInProjectTemplate',
  );

export interface CreateTaskBeforeInProjectTemplate {
  taskId: number;
  taskName?: string;
  projectPhaseId: number;
}

export const createTaskBeforeInProjectTemplate =
  createAction<CreateTaskBeforeInProjectTemplate>(
    '@@TEMPLATES/PROJECT/createTaskBeforeInProjectTemplate',
  );

export interface UpdateTaskInProjectTemplate extends BaseActionPayload {
  taskId: number;
  payload: Partial<ProjectTemplateTask>;
}
export const updateTaskInProjectTemplate =
  createAction<UpdateTaskInProjectTemplate>(
    '@@TEMPLATES/PROJECT/updateTaskInProjectTemplate',
  );

export const rearrangeEntitiesInProjectTemplate = createAction<PositionData[]>(
  '@@TEMPLATES/PROJECT/rearrangeEntitiesInProjectTemplate',
);
export interface TaskIdPayload extends BaseActionPayload {
  taskId: number;
  payload?: { taskId: number; taskName?: string };
}

export const deleteTaskInProjectTemplate = createAction<TaskIdPayload>(
  '@@TEMPLATES/PROJECT/deleteTaskInProjectTemplate',
);
export const addTaskBelowProjectTemplate = createAction<TaskIdPayload>(
  '@@TEMPLATES/PROJECT/addTaskBelowProjectTemplate',
);
export const addTaskAboveProjectTemplate = createAction<TaskIdPayload>(
  '@@TEMPLATES/PROJECT/addTaskAboveProjectTemplate',
);

export interface AddTaskDependencyInProjectTemplatePayload
  extends BaseActionPayload {
  taskId: number;
  dependentTaskId: number;
  dependencyType: string;
  lag: number;
}
export const addTaskDependencyInProjectTemplate =
  createAction<AddTaskDependencyInProjectTemplatePayload>(
    '@@TEMPLATES/PROJECT/addTaskDependencyInProjectTemplate',
  );

export const updateTaskDependenciesInProjectTemplate = createAction<{
  taskId: number;
  dependentTasks: {
    taskId: number;
    dependencyType: string;
    lag: number | null;
    taskName?: string;
  }[];
}>('@@TEMPLATES/PROJECT/updateTaskDependenciesInProjectTemplate');

export interface AddPhaseDependencyInProjectTemplatePayload
  extends BaseActionPayload {
  phaseId: number;
  dependentPhaseId: number;
  dependencyType: string;
  lag: number | null;
}
export const addPhaseDependencyInProjectTemplate =
  createAction<AddPhaseDependencyInProjectTemplatePayload>(
    '@@TEMPLATES/PROJECT/addPhaseDependencyInProjectTemplate',
  );

export const updatePhaseDependenciesInProjectTemplate = createAction<{
  phaseId: number;
  dependentPhases: {
    projectPhaseId: number;
    dependencyType: string;
    lag: number | null;
  }[];
}>('@@TEMPLATES/PROJECT/updatePhaseDependenciesInProjectTemplate');

// ---- SUB TASK ---- //
export interface CreateSubTaskInProjectTemplate extends BaseActionPayload {
  parentTaskId: number;
  payload?: { taskId: number; taskName?: '' };
  task?: any;
  insertAt?: any;
}
export const createSubTaskInProjectTemplate =
  createAction<CreateSubTaskInProjectTemplate>(
    '@@TEMPLATES/PROJECT/createSubTaskInProjectTemplate',
  );

// ---- SPRINT ---- //

export interface SprintIdPayload extends BaseActionPayload {
  sprintId: number;
}

export interface CreateSprintInProjectTemplate extends BaseActionPayload {
  sprint: AtLeast<ProjectTemplateSprint, 'sprintId'>;
  projectPhaseId: number;
  insertAt?: {
    id: string;
    dir: 0 | 1;
  };
}
export const createSprintInProjectTemplate =
  createAction<CreateSprintInProjectTemplate>(
    '@@TEMPLATES/PROJECT/createSprintInProjectTemplate',
  );

export interface UpdateSprintInProjectTemplate extends BaseActionPayload {
  sprintId: number;
  payload: Partial<ProjectTemplateSprint>;
}

export const updateSprintInProjectTemplate =
  createAction<UpdateSprintInProjectTemplate>(
    '@@TEMPLATES/PROJECT/updateSprintInProjectTemplate',
  );

export const deleteSprintInProjectTemplate = createAction<SprintIdPayload>(
  '@@TEMPLATES/PROJECT/deleteSprintInProjectTemplate',
);

// ---- SPACES ---- //
export interface CreateSpaceInProjectTemplatePayload extends BaseActionPayload {
  space: Partial<Pick<ProjectTemplateSpace, 'private'>>;
}
export const createSpaceInProjectTemplate =
  createAction<CreateSpaceInProjectTemplatePayload>(
    '@@TEMPLATES/PROJECT/createSpaceInProjectTemplate',
  );

export interface MinSpaceIdInProjectTemplatePayload extends BaseActionPayload {
  space: AtLeast<ProjectTemplateSpace, 'spaceId'>;
}
export const updateSpaceInProjectTemplate =
  createAction<MinSpaceIdInProjectTemplatePayload>(
    '@@TEMPLATES/PROJECT/updateSpaceInProjectTemplate',
  );

export const deleteSpaceInProjectTemplate =
  createAction<MinSpaceIdInProjectTemplatePayload>(
    '@@TEMPLATES/PROJECT/deleteSpaceInProjectTemplate',
  );

export interface SetupProjectTemplateSpace extends BaseActionPayload {
  companyName: string;
  privateSpaceName: string;
  sharedSpaceName: string;
}
export const setupSpaceInProjectTemplateAction =
  createAction<SetupProjectTemplateSpace>(
    '@@TEMPLATES/PROJECT/setupSpaceInProjectTemplateAction',
  );

// ---- SPACE TAB ---- //
export interface DeleteSpaceTabProjectTemplatePayload
  extends BaseActionPayload {
  spaceTabId: number;
  spaceId?: number;
}
export const deleteSpaceTabInProjectTemplateAction =
  createAction<DeleteSpaceTabProjectTemplatePayload>(
    '@@TEMPLATES/PROJECT/deleteSpaceTabInProjectTemplate',
  );

// ---- JIRA ---- //
export interface UpdateJiraMappingsInTemplateAction extends BaseActionPayload {
  taskId: number;
  data: Record<string, any> | undefined;
}
export const updateJiraMappingsInTemplateAction =
  createAction<UpdateJiraMappingsInTemplateAction>(
    '@@TEMPLATES/PROJECT/JiraMapping',
  );

// ---- DYNAMIC TEMPLATES ---- //

export interface UpdateDynamicTemplateConditionsActionPayload
  extends BaseActionPayload {
  entityId: number;
  conditions: Filter;
}
export const updateDynamicTemplateConditionsAction =
  createAction<UpdateDynamicTemplateConditionsActionPayload>(
    '@@TEMPLATES/PROJECT/dynamicConditions',
  );

export interface GetDynamicTemplatePreviewActionPayload
  extends BaseActionPayload {
  fields: any;
  onSuccess?: (templatePreview) => void;
  onFinally?: () => void;
  compressTimeline: boolean;
}
export const getDynamicTemplatePreviewAction =
  createAction<GetDynamicTemplatePreviewActionPayload>(
    '@@TEMPLATES/PROJECT/dynamic/preview',
  );
export interface AllocationBulkUpdatePayload extends BaseActionPayload {
  actionsMap: BulkActionPayloadMap;
  relativeDate: Moment;
  isHoursPerWeek?: boolean;
}
export const allocationsBulkAction = createAction<AllocationBulkUpdatePayload>(
  '@@TEMPLATES/PROJECT/allocation/bulkAction',
);

export interface UpdateAllocatedMembersPayload extends BaseActionPayload {
  users: User[];
  teams: Team[];
}
export const updateAllocatedMembersAction =
  createAction<UpdateAllocatedMembersPayload>(
    '@@TEMPLATES/PROJECT/allocation/updateMembers',
  );

// <--- Automations --->
export interface UpdateAllAutomationsPayload {
  automations: Automation[];
}

export interface UpdateAutomationById {
  automation: Automation;
  automationId: number;
}

export interface ToggleEnabledStateForAutomationByIdPayload {
  automationId: number;
  state: boolean;
}

export interface CreateAutomationForSourceActionPayload {
  source: AutomationSource;
  sourceId: number;
  automation: Automation;
}

export const updateAllAutomationAction =
  createAction<UpdateAllAutomationsPayload>(
    '@@TEMPLATES/AUTOMATIONS/updateAutomations/all',
  );

export const updateAutomationByIdAction = createAction<UpdateAutomationById>(
  '@@TEMPLATES/AUTOMATIONS/updateAutomations/byId',
);

export const createAutomationForSourceAction =
  createAction<CreateAutomationForSourceActionPayload>(
    '@@TEMPLATES/AUTOMATIONS/createAutomation/forSource',
  );

export const toggleEnabledStateForAutomationByIdAction =
  createAction<ToggleEnabledStateForAutomationByIdPayload>(
    '@@TEMPLATES/AUTOMATIONS/updateAutomations/toggleEnabled',
  );

// DEAD CODE - Todo @Muskaan- remove below code
export interface RevenueRecognitionActionPayload extends BaseActionPayload {
  milestoneRevenueRecognitionConfigs: MilestoneRevenueRecognitionConfigs[];
}

export const updateRevenueRecognitionAction =
  createAction<RevenueRecognitionActionPayload>(
    '@@TEMPLATES/REVENUE_RECOGNITION_MILESTONE/updateRevenueRecognition',
  );
